// 操作引导 · 示例模式 — every piece of sample data the guided tour shows, in one place.
//
// The tour walks the real screens (客户工作台, 今日跟进, 服务监督, 月度报告). While it runs, the
// TourProvider routes the shared axios instance through demoRespond() (services/api.js →
// setDemoHandler): GETs for the sample member / team are answered from here, every write is refused
// ("示例模式：不会保存"), and nothing reaches the server. Names are obviously fake (示例 · …, DEMO-xx)
// and every id lives in the 9090909xx range so a stray request can be recognised and kept local.

import { SOP_STEPS } from '../profiles/workspace/wsShared';

export const DEMO_MSG = '示例模式：不会保存';
export const DEMO_PROFILE_ID = 909090901;
export const DEMO_REPORT_ID = 909090951;
export const DEMO_COACHES = {
  amy: { id: 909090801, first_name: 'Amy', last_name: '· 示例' },
  grace: { id: 909090802, first_name: 'Grace', last_name: '· 示例' },
  kevin: { id: 909090803, first_name: 'Kevin', last_name: '· 示例' },
  linda: { id: 909090804, first_name: 'Linda', last_name: '· 示例' },
  mia: { id: 909090805, first_name: 'Mia', last_name: '· 示例' },
};
export const DEMO_KEVIN_ID = DEMO_COACHES.kevin.id;
const ID_MARK = '9090909';
export const isDemoId = (v) => String(v || '').includes(ID_MARK);

const H = 3600e3;
const DAY = 24 * H;
const iso = (ms) => new Date(ms).toISOString();
const fullName = (c) => `${c.first_name} ${c.last_name}`;
const AMY = DEMO_COACHES.amy;

export const DEMO_MEMBER_NAME = '示例 · 陈先生';
export const DEMO_SEED = { id: DEMO_PROFILE_ID, first_name: DEMO_MEMBER_NAME, last_name: '', offline_id: 'DEMO-01' };

// A fake call screenshot: "通话 12 分钟".
export const DEMO_SHOT = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="160" viewBox="0 0 120 160">'
  + '<rect width="120" height="160" rx="10" fill="#EFEAE2"/><rect x="10" y="12" width="100" height="10" rx="5" fill="#E4DFD7"/>'
  + '<circle cx="60" cy="62" r="20" fill="#D6D0C7"/><text x="60" y="112" font-size="13" text-anchor="middle" fill="#6E685F" font-family="sans-serif">示例截图</text>'
  + '<text x="60" y="134" font-size="12" text-anchor="middle" fill="#6E685F" font-family="sans-serif">通话 12 分钟</text></svg>',
)}`;

export const DEMO_CONTACT_TEXT = '本周已初筛 6 位，2 位在与对方老师核实婚育情况。会员同意年龄上限放宽到 36。下一步：周五前给推荐。';
export const DEMO_MEMO_TEXT = '我帮您向对方确认是否接受异地，明天给您答复。';

const pct = (num, den) => ({ num, den, pct: den ? Math.round((num / den) * 100) : null });
const startOfDay = (ms) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
const localDT = (ms) => {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:00`;
};

/** All fixtures, built relative to `now` once per tour run. route: 'coach' | 'supervisor'. */
export function buildFixtures(route = 'coach', now = Date.now()) {
  const sup = route === 'supervisor';
  const today0 = startOfDay(now);
  // 「已超 6 小时」 but still on today's chip.
  const dueMs = Math.max(today0 + 60e3, now - 6 * H);
  const serviceStart = now - 11 * DAY - 3 * H;
  const periodStart = now - 25 * DAY;
  const periodEnd = now + 5 * DAY;
  const reportStatus = sup ? 'submitted' : 'draft';
  const mon = new Date((periodStart + periodEnd) / 2).getMonth() + 1;

  const report = {
    id: DEMO_REPORT_ID, profile_id: DEMO_PROFILE_ID, period_index: 1,
    period_start: iso(periodStart), period_end: iso(periodEnd), due_at: iso(periodEnd), status: reportStatus,
    can_edit: !sup,
    stats: { contacts_two_way: 9, recommended: 3, interested: 2, meetings: 1 },
    opening: '陈先生，这是您加入 FINDU 后的第一份月度报告。这个月我们完成了您的画像，并开始了第一轮推荐。',
    timeline: [
      { date: iso(serviceStart + 1 * DAY), text: '完成 40 分钟人生合伙人画像', include: true, source: 'step', source_id: 1 },
      { date: iso(serviceStart + 3 * DAY), text: '完成爱的五种语言测评', include: true, source: 'step', source_id: 2 },
      { date: iso(now - 4 * DAY), text: '为您推荐 3 位人选，其中 2 位您愿意进一步了解', include: true, source: 'match', source_id: 3 },
      { date: iso(now - 2 * DAY), text: '与第一位人选正式见面', include: true, source: 'date', source_id: 4 },
    ],
    learned: '您更看重对方的稳定和责任感；对年龄的要求可以放宽到 36 岁。',
    plan: '周五前再推荐 2 位\n安排第二次见面前的沟通电话',
    internal_note: sup ? '会员这周情绪稳定，第二位人选在核实婚育情况。' : '',
    profile: { first_name: DEMO_MEMBER_NAME, last_name: '', offline_id: 'DEMO-01' },
    author: { id: AMY.id, first_name: AMY.first_name, last_name: AMY.last_name },
    author_user_id: AMY.id,
    submitted_at: sup ? iso(now - 20 * H) : null,
    checklist: {},
    updated_at: iso(now - 20 * H),
  };
  const reportRow = { ...report };

  const svc = {
    service_start_at: iso(serviceStart), service_day: 12, in_focus_period: true, interval_hours: 48, pause_until: null,
    pending_interval_request: null, last_contact_at: iso(dueMs - 48 * H), next_due_at: iso(dueMs), overdue_hours: 6,
    todos: { open: 1, overdue: 0 }, complaints: { open: 0, urgent_open: 0 },
    report: { id: DEMO_REPORT_ID, status: reportStatus, period_index: 1, period_start: report.period_start, period_end: report.period_end, due_at: report.due_at },
    can_approve: sup,
  };

  const startKeys = ['sign', 'group', 'portrait', 'love_language', 'quick_card', 'team_review'];
  const steps = SOP_STEPS.map((s, i) => {
    if (startKeys.includes(s.key)) {
      return { key: s.key, status: 'done', done_at: iso(serviceStart + i * DAY + 2 * H), done_by_name: fullName(AMY), round_step: false, done_source: 'auto' };
    }
    if (s.key === 'first_recs') return { key: s.key, status: 'current', due_at: iso(now + DAY - 2 * H), can_tick: true, round_step: true };
    return { key: s.key, status: 'todo', round_step: true, can_tick: false };
  });

  const nextActions = [
    { key: 'contact48', severity: 'red', action: 'contact', overdue: true, due_at: iso(dueMs),
      title: '48 小时没联系了，今天联系一次', why: '规则：前三个月没有合适推荐时，每 48 小时至少有效联系一次。',
      action_args: { purpose: 'followup_48h' } },
    { key: 'step:first_recs', severity: 'amber', action: 'step', due_at: iso(now + DAY - 2 * H),
      title: '首批推荐 3 位（两位老师核实）', why: '画像后 3 天内完成；每位都要有具体推荐理由，第二位老师核实。',
      action_args: { step_key: 'first_recs' } },
    { key: 'report', severity: 'normal', action: 'report', due_at: report.due_at,
      title: `${mon} 月服务报告 · ${sup ? '待审核' : '待起草'}`, why: '每 30 天一份服务报告，主管审核后发给会员。',
      action_args: { report_id: DEMO_REPORT_ID } },
  ];

  const ws = {
    profile: { offline_id: 'DEMO-01', age: 36, city: '洛杉矶' },
    service_day: 12, stage_name: '首批推荐',
    light: sup ? 'red' : 'yellow',
    light_reasons: sup ? ['48 小时联系超时（已超 6 小时）', '1 件答应会员的事没按时做'] : ['48 小时没联系，今天要联系', '首批推荐还剩 1 天'],
    is_supervisor: sup, status: svc, steps, round: 1, rounds: [], next_actions: nextActions,
    reviews_pending: sup ? { contacts: 2, interval_requests: 0, reports: 1, rectifications: 0 } : { contacts: 0, interval_requests: 0, reports: 0, rectifications: 0 },
    rectifications: [],
    service_closed: false,
  };

  const profile = {
    id: DEMO_PROFILE_ID, first_name: DEMO_MEMBER_NAME, last_name: '', offline_id: 'DEMO-01', gender: 'male',
    date_of_birth: `${new Date(now).getFullYear() - 36}-01-15`, location_city: '洛杉矶', location_state: 'CA',
    pipeline_stage: 'matching', status: 'active', assigned_coach_id: AMY.id, matchmaker_id: AMY.id,
    coach_first_name: AMY.first_name, coach_last_name: AMY.last_name, plan_name: '示例 · VIP 一对一',
    membership_end_date: new Date(now + 200 * DAY).toISOString().slice(0, 10), custom_values: {}, customFields: [],
  };

  const contact = (id, ms, text, extra = {}) => ({
    type: 'contact', id, text, at: iso(ms), contacted_at: iso(ms), by: fullName(AMY), by_user_id: AMY.id,
    purpose: 'followup_48h', method: 'phone', outcome: 'two_way', review_status: 'approved', images: [DEMO_SHOT], ...extra,
  });
  const activity = [
    contact(909090971, now - 2 * DAY - 6 * H, DEMO_CONTACT_TEXT.replace('下一步：周五前给推荐。', ''), { review_status: 'submitted' }),
    contact(909090972, now - 4 * DAY, '推荐了 3 位人选，逐一说明了匹配点和可能的顾虑；会员想先了解第一位。', { purpose: 'recommendation', review_status: 'submitted' }),
    contact(909090973, now - 7 * DAY, '确认画像结果，会员补充：希望对方有稳定工作，可以接受异地半年。', { purpose: 'followup_48h' }),
    { type: 'note', id: 909090974, text: '会员周末一般不方便接电话，工作日晚上 8 点后联系。', at: iso(now - 9 * DAY), by: fullName(AMY), by_user_id: AMY.id },
  ];

  const notes = {
    notes: [{
      id: 909090961, profile_id: DEMO_PROFILE_ID, note: '周五前补充推荐 2 位', text: '周五前补充推荐 2 位',
      remind_at: iso(now + 2 * DAY), state: 'open', assignee_user_id: AMY.id, assignee_name: fullName(AMY),
      created_by: AMY.id, created_by_name: fullName(AMY), created_at: iso(now - DAY), images: [],
    }],
    todo_counts: { open: 1, overdue: 0 },
    authors: [{ id: AMY.id, name: fullName(AMY) }],
  };

  const kpiCheck = {
    items: [
      { key: 'contact_48h', label: '48 小时联系达标', ok: false, num: 3, den: 5, detail: '有 2 次超过 48 小时没联系' },
      { key: 'cross_call', label: '见面前交叉电话', ok: true, num: 1, den: 1 },
      { key: 'date_feedback', label: '约会后反馈', ok: true, num: 1, den: 1 },
      { key: 'promises', label: '承诺按时完成', ok: false, num: 1, den: 2, detail: '1 件答应会员的事没按时做' },
      { key: 'complaints', label: '投诉已处理', ok: true },
      { key: 'quick_card', label: '速记卡完整', ok: null, num: 10, den: 16 },
    ],
  };

  const rectifications = {
    items: [{
      id: 909090981, profile_id: DEMO_PROFILE_ID, coach_user_id: AMY.id, coach_name: fullName(AMY),
      title: '今天内补打 48 小时电话，并写清会员反馈和下一步', due_at: iso(now - 3 * DAY), status: 'done',
      done_note: '已补打，会员同意先了解 2 位', created_by_name: '示例主管', created_at: iso(now - 5 * DAY),
    }],
  };

  const quickCard = {
    fields: {
      member_no: 'DEMO-01', age: '36', city: '洛杉矶', marriage_children: '未婚 · 无子女', occupation: '软件工程师',
      finance: '年收入稳定，有房', personality: '稳重、慢热、做事有计划', strengths: '责任感强，生活规律',
      criteria: '30–36 岁，本科以上，愿意定居洛杉矶', love_language: '精心时刻',
    },
    history: [], updated_by_name: fullName(AMY), updated_at: iso(now - 6 * DAY),
  };

  const staff = { staff: Object.values(DEMO_COACHES).slice(0, 3).map((c) => ({ ...c, role: 'coach' })) };

  // ── 今日跟进 (CoachFollowupBar rows) ──
  const member = (id, name, no, extra) => ({ profile_id: id, first_name: name, last_name: '', offline_id: no, profile_photo: null, ...extra });
  const followups = [
    member(DEMO_PROFILE_ID, DEMO_MEMBER_NAME, 'DEMO-01', { buckets: ['no_contact'], next_due_at: iso(dueMs), overdue_hours: 6, interval_hours: 48, days_since_contact: 2, tour_target: 'followup-demo' }),
    member(909090902, '示例 · 林女士', 'DEMO-02', { buckets: ['meeting'], next_meeting_at: localDT(today0 + 19 * H), days_since_contact: 1 }),
    member(909090903, '示例 · 李女士', 'DEMO-03', { buckets: ['week', 'todo'], next_contact_at: iso(today0 + 23 * H), todo_due_at: iso(now + 2 * DAY), days_since_contact: 3, reasons: [{ bucket: 'todo', text: '周五前补充推荐 2 位' }] }),
    member(909090904, '示例 · 王先生', 'DEMO-04', { buckets: ['overdue', 'todo'], next_contact_at: iso(today0 - 2 * DAY + 10 * H), todo_due_at: iso(now + DAY), days_since_contact: 4, reasons: [{ bucket: 'todo', text: '确认是否接受异地' }] }),
    member(909090905, '示例 · 赵女士', 'DEMO-05', { buckets: ['overdue'], next_contact_at: iso(today0 - DAY + 10 * H), days_since_contact: 3 }),
    member(909090906, '示例 · 周先生', 'DEMO-06', { buckets: ['week'], next_contact_at: iso(today0 + DAY + 11 * H), days_since_contact: 1 }),
    member(909090907, '示例 · 孙女士', 'DEMO-07', { buckets: ['no_meeting'], days_since_contact: 2, reasons: [{ bucket: 'no_meeting', text: '签约 9 天还没有约见' }] }),
  ];

  // ── 服务监督 ──
  const kpi = (c48, cross, fb, prom, rep, qc, acc, extra = {}) => ({
    contact_48h: pct(...c48), cross_call: pct(...cross), date_feedback: pct(...fb), promises: pct(...prom),
    reports_on_time: pct(...rep), quick_card_complete: pct(...qc), accept_rate: pct(...acc),
    complaints_open: 0, recommendations: 0, meetings: 0, ...extra,
  });
  const coachRow = (c, o) => ({ id: c.id, name: fullName(c), photo: null, active: true, contacts_two_way: 20, contacts_no_reply: 4, rectifications_overdue: 0, ...o });
  const coaches = [
    coachRow(DEMO_COACHES.kevin, { members: 41, red: 6, yellow: 4, green: 31, score: 55, pending_reviews: 3, rectifications_open: 2, rectifications_overdue: 1,
      kpi: kpi([49, 100], [2, 2], [3, 4], [2, 4], [5, 6], [30, 41], [0, 0]),
      flags: ['6 位会员红灯', '48 小时联系 49%', '承诺按时 50%', '本期没有推荐'],
      tour_target: 'sv-coach-kevin',
      tour: { route: 'coach', last_step: 0, total: 7, completed: false, outdated: false } }),
    coachRow(DEMO_COACHES.amy, { members: 38, red: 3, yellow: 5, green: 30, score: 72, pending_reviews: 4, rectifications_open: 1,
      kpi: kpi([68, 100], [3, 3], [4, 5], [7, 9], [6, 7], [33, 38], [5, 9], { recommendations: 9, meetings: 3 }),
      flags: ['3 位会员红灯'],
      tour: { route: 'coach', last_step: 7, total: 7, completed: true, outdated: false } }),
    coachRow(DEMO_COACHES.linda, { members: 30, red: 2, yellow: 6, green: 22, score: 81, pending_reviews: 1, rectifications_open: 1,
      kpi: kpi([82, 100], [2, 2], [2, 3], [8, 9], [5, 5], [26, 30], [4, 8], { recommendations: 8, meetings: 2 }),
      flags: ['2 位会员红灯'],
      tour: { route: 'coach', last_step: 7, total: 7, completed: true, outdated: true } }),
    coachRow(DEMO_COACHES.grace, { members: 24, red: 1, yellow: 2, green: 21, score: 91, pending_reviews: 1, rectifications_open: 0,
      kpi: kpi([94, 100], [2, 2], [2, 2], [9, 10], [4, 4], [23, 24], [6, 9], { recommendations: 9, meetings: 2 }),
      flags: [],
      tour: { route: 'coach', last_step: 4, total: 7, completed: false, outdated: false } }),
  ];
  const supervision = {
    period: { from: iso(now - 29 * DAY).slice(0, 10), to: iso(now).slice(0, 10), days: 30 },
    is_supervisor: true,
    team: {
      members: 704, red: 17, yellow: 42, green: 645, pending_reviews: 9, rectifications_open: 4, rectifications_overdue: 1,
      contacts_two_way: 320, contacts_no_reply: 41, coaches: 9, score: 74,
      kpi: kpi([76, 100], [18, 20], [22, 26], [61, 75], [40, 44], [610, 704], [38, 80], { recommendations: 80, meetings: 26 }),
    },
    coaches,
  };
  const coachDetail = (id) => {
    const c = coaches.find((x) => Number(x.id) === Number(id)) || coaches[0];
    return {
      coach: { id: c.id, name: c.name, photo: null }, kpi: c.kpi, score: c.score, flags: c.flags,
      summary: { contacts_two_way: c.contacts_two_way, contacts_no_reply: c.contacts_no_reply },
      members: [
        { profile_id: DEMO_PROFILE_ID, name: DEMO_MEMBER_NAME, offline_id: 'DEMO-01', service_day: 12, light: 'red',
          light_reasons: ['48 小时联系超时（已超 6 小时）'], stage_name: '首批推荐', last_contact_at: iso(dueMs - 48 * H), overdue_hours: 6,
          todos_open: 1, todos_overdue: 0, pending_reviews: 3, rectifications_open: 0 },
        { profile_id: 909090902, name: '示例 · 林女士', offline_id: 'DEMO-02', service_day: 40, light: 'green', light_reasons: [], stage_name: '约见',
          last_contact_at: iso(now - DAY), todos_open: 0, pending_reviews: 0, rectifications_open: 0 },
      ],
    };
  };
  const reviewQueue = {
    items: [
      { profile_id: DEMO_PROFILE_ID, name: DEMO_MEMBER_NAME, offline_id: 'DEMO-01', coach_name: fullName(AMY), light: 'red',
        contacts_submitted: 2, interval_requests: 0, reports_submitted: 1, rectifications_to_verify: 1, rectifications_open: 0, pending_total: 3 },
      { profile_id: 909090902, name: '示例 · 林女士', offline_id: 'DEMO-02', coach_name: fullName(DEMO_COACHES.grace), light: 'green',
        contacts_submitted: 1, interval_requests: 1, reports_submitted: 0, rectifications_open: 0, pending_total: 2 },
    ],
    counts: { total: 9, contacts: 5, interval_requests: 1, reports: 2, rectifications: 1 },
  };
  const records = {
    items: activity.filter((x) => x.type === 'contact').map((x) => ({
      type: 'contact', id: x.id, at: x.at, coach_name: x.by, profile_id: DEMO_PROFILE_ID, member_name: DEMO_MEMBER_NAME, offline_id: 'DEMO-01',
      title: '48 小时跟进', purpose: x.purpose, method: x.method, outcome: x.outcome, text: x.text, images: x.images, review_status: x.review_status,
    })),
    total: 3, page: 1, page_size: 30, by_type: { contact: 3, note: 1, rectification: 1, step: 6, quickcard: 0, complaint: 0 },
  };
  const reportList = {
    reports: [
      { ...reportRow, status: 'submitted', submitted_at: iso(now - 20 * H) },
      { id: 909090952, profile_id: 909090902, period_index: 2, period_start: iso(periodStart), period_end: iso(periodEnd), due_at: iso(periodEnd),
        status: 'submitted', submitted_at: iso(now - 2 * DAY), profile: { first_name: '示例 · 林女士', offline_id: 'DEMO-02' },
        author: { first_name: 'Grace', last_name: '· 示例' }, stats: { contacts_two_way: 7, recommended: 2, interested: 1, meetings: 1 } },
      { id: 909090953, profile_id: 909090905, period_index: 1, period_start: iso(periodStart - 30 * DAY), period_end: iso(periodEnd - 30 * DAY), due_at: iso(periodEnd - 30 * DAY),
        status: 'published', submitted_at: iso(now - 30 * DAY), published_at: iso(now - 29 * DAY), profile: { first_name: '示例 · 赵女士', offline_id: 'DEMO-05' },
        author: { first_name: 'Amy', last_name: '· 示例' } },
    ],
  };
  const reportById = (id) => {
    if (Number(id) === DEMO_REPORT_ID) return { report: sup ? { ...report, status: 'submitted', can_edit: false, submitted_at: iso(now - 20 * H) } : report };
    const r = reportList.reports.find((x) => Number(x.id) === Number(id));
    return { report: r ? { ...r, opening: '（示例报告）', timeline: [], learned: '', plan: '' } : report };
  };

  // 会员心动 (GET /profiles/:id/member-favorites): one 互相心动 not yet recommended (→ the ① 下一步 nudge),
  // one already recommended, one removed again.
  const favTarget = (id, first, age, city, gender = 'female') => ({ id, first_name: first, last_name: '', offline_id: `DEMO-${String(id).slice(-2)}`, photo: null, gender, age, city });
  const favLin = favTarget(909090902, '示例 · 林女士', 31, 'Irvine');
  const favZhou = favTarget(909090911, '示例 · 周女士', 29, 'Los Angeles');
  const favWu = favTarget(909090912, '示例 · 吴女士', 33, 'San Diego');
  const memberFavorites = {
    current: [
      { target: favZhou, favorited_at: iso(now - 5 * H), mutual: true, recommended: false, dated: false },
      { target: favLin, favorited_at: iso(now - 3 * DAY), mutual: false, recommended: true, dated: true },
    ],
    events: [
      { action: 'add', at: iso(now - 5 * H), target: favZhou },
      { action: 'remove', at: iso(now - 2 * DAY), target: favWu },
      { action: 'add', at: iso(now - 2 * DAY - 2 * H), target: favWu },
      { action: 'add', at: iso(now - 3 * DAY), target: favLin },
    ],
    counts: { current: 2, mutual: 1 },
  };

  return {
    route, profile, ws, svc, activity, notes, kpiCheck, rectifications, quickCard, staff, followups,
    supervision, coachDetail, reviewQueue, records, reportList, reportById, report, memberFavorites,
  };
}

// GET routes answered from the fixtures. [method-less regex on the path, (match, params) → data]
const ROUTES = [
  [/^\/profiles\/9090909\d+\/workspace$/, (fx) => fx.ws],
  [/^\/profiles\/9090909\d+\/service-status$/, (fx) => fx.svc],
  [/^\/profiles\/9090909\d+\/activity$/, (fx) => ({ activity: fx.activity })],
  [/^\/profiles\/9090909\d+\/notes$/, (fx) => fx.notes],
  [/^\/profiles\/9090909\d+\/kpi-check$/, (fx) => fx.kpiCheck],
  [/^\/profiles\/9090909\d+\/rectifications$/, (fx) => fx.rectifications],
  [/^\/profiles\/9090909\d+\/quick-card$/, (fx) => fx.quickCard],
  [/^\/profiles\/9090909\d+\/member-favorites$/, (fx) => fx.memberFavorites],
  [/^\/profiles\/9090909\d+\/service-reports$/, (fx) => ({ reports: [fx.reportById(DEMO_REPORT_ID).report] })],
  [/^\/profiles\/9090909\d+$/, (fx) => fx.profile],
  [/^\/service-reports\/(\d+)$/, (fx, m) => fx.reportById(m[1])],
  [/^\/service-reports$/, (fx) => fx.reportList],
  [/^\/service\/supervision\/coach\/(\d+)$/, (fx, m) => fx.coachDetail(m[1])],
  [/^\/service\/supervision$/, (fx) => fx.supervision],
  [/^\/service\/review-queue$/, (fx) => fx.reviewQueue],
  [/^\/service\/records$/, (fx) => fx.records],
  [/^\/service\/staff$/, (fx) => fx.staff],
  [/^\/coach\/followup-alerts$/, (fx) => ({ clients: fx.followups })],
];

// Writes that may still go out while the tour runs: the tour's own progress and auth.
const WRITE_ALLOW = [/^\/service\/tour\/(coach|supervisor)$/, /^\/auth\//];

const pathOf = (url) => {
  let u = String(url || '');
  if (/^https?:\/\//i.test(u)) { try { u = new URL(u).pathname.replace(/^\/api(?=\/)/, ''); } catch (e) { /* keep */ } }
  return u.split('?')[0];
};

/**
 * axios request → undefined (send for real) | { data } | { block: true, message }.
 * fx: buildFixtures(...) result.
 */
export function demoRespond(fx, config) {
  const method = String((config && config.method) || 'get').toLowerCase();
  const path = pathOf(config && config.url);
  if (method !== 'get' && method !== 'head') {
    if (WRITE_ALLOW.some((re) => re.test(path))) return undefined;
    return { block: true, message: DEMO_MSG };
  }
  for (const [re, fn] of ROUTES) {
    const m = path.match(re);
    if (m) return { data: fn(fx, m, (config && config.params) || {}) };
  }
  // Anything else about a sample member / coach stays local and comes back empty.
  let params = '';
  try { params = JSON.stringify((config && config.params) || {}); } catch (e) { params = ''; }
  if (isDemoId(path) || params.includes(ID_MARK)) return { data: {} };
  return undefined;
}
